import { type FC, useMemo } from 'react';
import type { ChatBubbleProps } from './ChatBubble.types';
import {
  Avatar,
  Pill,
  Button,
  ButtonGroup,
  Flex,
  Text,
  Timestamp,
  ButtonIntent,
} from '@/_components';
import { IconType } from '@/_types/icons';
import { getClasses } from '@/_utils/classnames';
import styles from './ChatBubble.module.css';
import { getDateAgo } from '@/_utils/dates';

export const ChatBubble: FC<ChatBubbleProps> = ({
  avatarName,
  avatarUrl,
  children,
  date,
  firm,
  id,
  isActive,
  isMe,
  isReply,
  isScheduled,
  isAIResponse,
  onOpenThread,
  tag,
  unreadThreadMessageCount = 0,
  isEdited,
}) => {
  const chatBubbleClasses: string = getClasses(styles.chatBubble, {
    [styles.chatBubbleMe]: isMe && !isAIResponse,
    [styles.chatBubbleAI]: isMe && isAIResponse,
    [styles.chatBubbleActive]: isActive,
    [styles.chatBubbleReply]: isReply,
    [styles.chatBubbleUnread]: unreadThreadMessageCount > 0,
  });

  const dateAgo = getDateAgo(date);
  const hasTag: boolean = tag !== 'general';

  const icon = useMemo((): IconType => {
    switch (tag) {
      case 'urgent':
        return 'siren';
      default:
        return 'warning';
    }
  }, [tag]);

  const color = useMemo((): ButtonIntent => {
    switch (tag) {
      case 'urgent':
        return 'danger';
      default:
        return 'danger-secondary';
    }
  }, [tag]);

  return (
    <Flex
      className={chatBubbleClasses}
      data-component="ChatBubble"
      gap={8}
      id={`message-${id}`}
    >
      <Avatar
        size={32}
        name={avatarName}
        type={isMe ? 'me' : 'user'}
        avatarUrl={avatarUrl}
      />
      <div className={styles.chatBubble__content}>
        {unreadThreadMessageCount > 0 && (
          <Flex isCentered={true} className={styles.chatBubble__unreadCount}>
            {unreadThreadMessageCount}
          </Flex>
        )}
        <Flex isColumn={true} gap={2}>
          <Flex isBetween={true} isColumn={true}>
            <Flex isColumn={true} gap={2} className={styles.chatBubble__header}>
              <Flex gap={2} isBetween={true}>
                <Flex gap={2} isYCentered={true}>
                  <Text type="small" fontWeight="bold">
                    {avatarName}
                  </Text>
                  {firm && (
                    <Pill size="small" intent="secondary" label={firm} />
                  )}
                  {isAIResponse && <Pill intent="warning" label="Draft" />}
                </Flex>
                <ButtonGroup className={styles.chatBubble__threadButtons}>
                  {hasTag && (
                    <Button
                      intent={color}
                      iconLeft={icon}
                      withTooltip={false}
                      value={tag}
                      size="tiny"
                    />
                  )}
                  {!isReply && (
                    <Button
                      onClick={onOpenThread}
                      intent="ghost"
                      size="tiny"
                      isDisabled={isScheduled}
                      isIconOnly={true}
                      iconLeft="chat"
                      withTooltip={true}
                      value="Reply in Thread"
                    />
                  )}
                </ButtonGroup>
              </Flex>
            </Flex>
            <Flex gap={4} isYCentered={true}>
              <Text color="secondary" type="tiny">
                {dateAgo}
              </Text>
              <Text color="disabled" type="tiny">
                &middot;
              </Text>
              <Text color="secondary" type="tiny">
                <Timestamp timestamp={date} />
              </Text>
              {isEdited && (
                <>
                  <Text color="disabled" type="tiny">
                    &middot;
                  </Text>
                  <Text color="secondary" type="tiny">
                    (Edited)
                  </Text>
                </>
              )}
            </Flex>
          </Flex>
          <div className={styles.chatBubble__content__message}>{children}</div>
        </Flex>
      </div>
    </Flex>
  );
};
